/**
 * Custom Error Classes for Auth and API Operations
 */

/**
 * Error thrown when authentication is required but not present
 */
export class AuthRequiredError extends Error {
  public readonly code = 'AUTH_REQUIRED';
  public readonly redirectUrl?: string;
  public readonly originalAction?: string;
  
  constructor(
    message: string = 'Authentication required',
    options?: {
      redirectUrl?: string;
      originalAction?: string;
    }
  ) {
    super(message);
    this.name = 'AuthRequiredError';
    this.redirectUrl = options?.redirectUrl;
    this.originalAction = options?.originalAction;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthRequiredError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      redirectUrl: this.redirectUrl,
      originalAction: this.originalAction,
    };
  }
}

/**
 * Error thrown when user lacks permission for an operation
 */
export class PermissionDeniedError extends Error {
  public readonly code = 'PERMISSION_DENIED';
  public readonly resource?: string;
  public readonly requiredPermission?: string;
  
  constructor(
    message: string = 'Permission denied',
    options?: {
      resource?: string;
      requiredPermission?: string;
    }
  ) {
    super(message);
    this.name = 'PermissionDeniedError';
    this.resource = options?.resource;
    this.requiredPermission = options?.requiredPermission;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PermissionDeniedError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      resource: this.resource,
      requiredPermission: this.requiredPermission,
    };
  }
}

/**
 * Error thrown when a resource is not found
 */
export class NotFoundError extends Error {
  public readonly code = 'NOT_FOUND';
  public readonly resourceType?: string;
  public readonly resourceId?: string;
  
  constructor(
    message: string = 'Resource not found',
    options?: {
      resourceType?: string;
      resourceId?: string;
    }
  ) {
    super(message);
    this.name = 'NotFoundError';
    this.resourceType = options?.resourceType;
    this.resourceId = options?.resourceId;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NotFoundError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      resourceType: this.resourceType,
      resourceId: this.resourceId,
    };
  }
}

/**
 * Error thrown when validation fails
 */
export class ValidationError extends Error {
  public readonly code = 'VALIDATION_ERROR';
  public readonly field?: string;
  public readonly value?: any;
  
  constructor(
    message: string = 'Validation failed',
    options?: {
      field?: string;
      value?: any;
    }
  ) {
    super(message);
    this.name = 'ValidationError';
    this.field = options?.field;
    this.value = options?.value;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      field: this.field,
    };
  }
}

/**
 * Error thrown for network/offline issues
 */
export class NetworkError extends Error {
  public readonly code = 'NETWORK_ERROR';
  public readonly isOffline: boolean;
  public readonly retryable: boolean;
  
  constructor(
    message: string = 'Network error',
    options?: {
      isOffline?: boolean;
      retryable?: boolean;
    }
  ) {
    super(message);
    this.name = 'NetworkError';
    this.isOffline = options?.isOffline ?? false;
    this.retryable = options?.retryable ?? true;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NetworkError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      isOffline: this.isOffline,
      retryable: this.retryable,
    };
  }
}

/**
 * Error thrown when session has expired
 */
export class SessionExpiredError extends Error {
  public readonly code = 'SESSION_EXPIRED';
  public readonly expiredAt?: number;
  
  constructor(
    message: string = 'Session expired',
    options?: {
      expiredAt?: number;
    }
  ) {
    super(message);
    this.name = 'SessionExpiredError';
    this.expiredAt = options?.expiredAt;
    
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SessionExpiredError);
    }
  }
  
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      expiredAt: this.expiredAt,
    };
  }
}

/**
 * Check if an error is an AuthRequiredError
 */
export function isAuthRequiredError(error: unknown): error is AuthRequiredError {
  return error instanceof AuthRequiredError || 
    (error instanceof Error && (error as any).code === 'AUTH_REQUIRED');
}

/**
 * Check if an error is a PermissionDeniedError
 */
export function isPermissionDeniedError(error: unknown): error is PermissionDeniedError {
  return error instanceof PermissionDeniedError ||
    (error instanceof Error && (error as any).code === 'PERMISSION_DENIED');
}

/**
 * Check if an error is a NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError ||
    (error instanceof Error && (error as any).code === 'NETWORK_ERROR');
}

/**
 * Check if an error is a SessionExpiredError
 */
export function isSessionExpiredError(error: unknown): error is SessionExpiredError {
  return error instanceof SessionExpiredError ||
    (error instanceof Error && (error as any).code === 'SESSION_EXPIRED');
}

/**
 * Convert API error response to appropriate error class
 */
export function apiErrorToException(
  statusCode: number,
  errorBody: any
): Error {
  const message = errorBody?.error || errorBody?.message || 'Unknown error';
  
  switch (statusCode) {
    case 401:
      return new AuthRequiredError(message);
    case 403:
      return new PermissionDeniedError(message, {
        resource: errorBody?.resource,
      });
    case 404:
      return new NotFoundError(message, {
        resourceType: errorBody?.resourceType,
        resourceId: errorBody?.resourceId,
      });
    case 400:
      return new ValidationError(message, {
        field: errorBody?.field,
      });
    default:
      return new Error(message);
  }
}
